import React, { useEffect, useState, useMemo, useContext, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../../stores/store';
import { useLocation } from 'react-router-dom';
import ListLayout from '../../Generic/ListLayout';
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import useDeleteItem from '../../../hooks/useDeleteItem';
import useLocalizedTextsMap from '../../../hooks/useLocalizedTextsMap';
import { useSetMenu } from "../../../hooks/useSetMenu";
import {createNewGeneralColumn, createSpecialActionColumn } from '../../Generic/dataGridColumns';
import AuthContext from "../../../context/AuthProvider";
import useFetchPaginatedDataWithFilters from '../../../hooks/useFetchPaginatedDataWithFilters';
import DataTablePagination from '../../Generic/DataTablePagination';
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";
import FormSelect from "../../Generic/FormSelect";
import { Link } from "react-router-dom";

const ReplacementScheduleList = () => {
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useContext(AuthContext);
  const { mainStore, schedulePlanStore } = useStore();
  const { t } = useTranslation();
  const localizedTextsMap = useLocalizedTextsMap();
  const { enqueueSnackbar } = useSnackbar();

  useSetMenu(mainStore.ReplacementScheduleMenuType, mainStore.ReplacementScheduleMenuNo);

  const location = useLocation();

  const { register, setValue, formState: { errors } } = useForm({
  });


  const [searchQuery, setSearchQuery] = useState(location.state?.search || "");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const deleteSchedulePlan = useDeleteItem(axiosPrivate, '/SchedulePlans', schedulePlanStore.deleteSchedulePlan);

  const [academicYears, setAcademicYears] = useState([]);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState(null);

  const [semesters, setSemesters] = useState([]);
  const [selectedSemesterId, setSelectedSemesterId] = useState(null);

  const [selectedFacultyId, setSelectedFacultyId] = useState(null);
  const [faculties, setFaculties] = useState([]);

  const [levels, setLevels] = useState([]);
  const [selectedLevelId, setSelectedLevelId] = useState(null);

  const [semesterTypes, setSemesterTypes] = useState([]);
  const [selectedSemesterTypeId, setSelectedSemesterTypeId] = useState(null);
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

  const defaultAcademicYearSet = useRef(false);


  const loadSemesters = async (semesterTypeId) => {
    try {
      const response = await axiosPrivate.get(
        `/StaffPortalStaffs/GetSelectSemester/${auth.universityId}/${auth.userId}/${semesterTypeId || ''}`
      );
      setSemesters(response.data);
      setSelectedSemesterId(null);
    } catch (err) {
      enqueueSnackbar(t("loadDataError") + ":" + err, { variant: "error" });
    }
  };

  const loadFaculties = async (academicYearId = null) => {
    try {
      const url = academicYearId
        ? `/StaffPortalStaffs/GetSelectFaculty/${auth.universityId}/${auth.userId}?academicYearId=${academicYearId}`
        : `/StaffPortalStaffs/GetSelectFaculty/${auth.universityId}/${auth.userId}`;
      const response = await axiosPrivate.get(url);
      setFaculties(response.data);

      if (selectedFacultyId && !response.data.some(f => f.value === selectedFacultyId.value)) {
        setSelectedFacultyId(null);
        setValue('facultyId', null);
      }
    } catch (err) {
      enqueueSnackbar(t("loadDataError") + ":" + err, { variant: "error" });
    }
  };

  const loadLevels = async (academicYearId = null) => {
    try {
      const url = academicYearId
        ? `/StaffPortalStaffs/GetSelectLevel/${auth.universityId}/${auth.userId}?academicYearId=${academicYearId}`
        : `/StaffPortalStaffs/GetSelectLevel/${auth.universityId}/${auth.userId}`;
      const response = await axiosPrivate.get(url);
      setLevels(response.data);

      if (selectedLevelId && !response.data.some(l => l.value === selectedLevelId.value)) {
        setSelectedLevelId(null);
        setValue('levelId', null);
      }
    } catch (err) {
      enqueueSnackbar(t("loadDataError") + ":" + err, { variant: "error" });
    }
  };

  const loadSelectOptions = async () => {
    try {
      const [academicYearsResponse, facultiesResponse, levelResponse, semesterTypeResponse] = await Promise.all([
        axiosPrivate.get(`/StaffPortalStaffs/GetSelectAcademicYearInSemesterPlan/${auth.universityId}/${auth.userId}`),
        axiosPrivate.get(`/StaffPortalStaffs/GetSelectFaculty/${auth.universityId}/${auth.userId}`),
        axiosPrivate.get(`/StaffPortalStaffs/GetSelectLevel/${auth.universityId}/${auth.userId}`),
        axiosPrivate.get(`/StaffPortalStaffs/GetSelectSemesterType/${auth.universityId}/${auth.userId}`),

      ]);

      const academicYearsList = academicYearsResponse.data || [];
      setAcademicYears(academicYearsList);

      let defaultAcademicYearValue = null;
      if (academicYearsList.length > 0 && !defaultAcademicYearSet.current) {
        const defaultAcademicYear = academicYearsList[0];
        setSelectedAcademicYearId(defaultAcademicYear);
        setValue('academicYearId', defaultAcademicYear.value, { shouldValidate: true });
        defaultAcademicYearSet.current = true;
        defaultAcademicYearValue = defaultAcademicYear.value;
      }

      if (defaultAcademicYearValue) {
        await loadFaculties(defaultAcademicYearValue);
        await loadLevels(defaultAcademicYearValue);
      } else {
        setFaculties(facultiesResponse.data);
        setLevels(levelResponse.data);
      }

      const semesterTypesData = semesterTypeResponse.data;
      setSemesterTypes(semesterTypesData);
      const today = new Date();
      const startDate = new Date('2025-02-15');
      const endDate = new Date('2025-08-15');
      const selectedType = today >= startDate && today <= endDate ? 2 : 1;
      const selectedOption = semesterTypesData.find(item => item.value == selectedType);
      if (selectedOption) {
        setSelectedSemesterTypeId(selectedOption);
        await loadSemesters(selectedOption.value);
      } else {
        await loadSemesters();
      }

      setIsInitialLoadComplete(true);

    } catch (err) {
      enqueueSnackbar(t("loadDataError") + ":" + err, { variant: "error" });
      setIsInitialLoadComplete(true);
    }
  };

  const handleSelectChange = async (field, selectedOption) => {
    setValue(field, selectedOption?.value, { shouldValidate: true });
    switch (field) {
      case 'academicYearId':
        setSelectedAcademicYearId(selectedOption);
        if (selectedOption) {
          await Promise.all([
            loadFaculties(selectedOption.value),
            loadLevels(selectedOption.value)
          ]);
        } else {
          await Promise.all([
            loadFaculties(),
            loadLevels()
          ]);
        }
        break;
      case 'semesterId':
        setSelectedSemesterId(selectedOption);
        break;
      case 'facultyId':
        setSelectedFacultyId(selectedOption);
        break;
      case 'levelId':
        setSelectedLevelId(selectedOption);
        break;
      case 'semesterTypeId':
        setSelectedSemesterTypeId(selectedOption);
        loadSemesters(selectedOption?.value);
        break;
      default:
        break;
    }
    setPage(0);
  };
  useEffect(() => {
    loadSelectOptions();
  }, [t, auth]);


  const columns = useMemo(() => {
    const replacementActionColumn = {
      field: "action",
      headerName: t("action"),
      sortable: false,
      flex: 0.5,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      headerClassName: 'tableHeaderClassName',
      cellClassName: 'tableCellClassName',
      renderHeader: (params) => (
        <span style={{ fontWeight: 600 }}>{params.colDef.headerName}</span>
      ),
      renderCell: (params) => (
        <div className="d-flex justify-content-center align-items-center">
          <Link to={`/SchedulePlan/SchedulePlanWeekListByPlanId/${params.row.id}?isReplacement=true`} className="me-3">
            <i className="bi bi-calendar-check text-brand fs-4"></i>
          </Link>
        </div>
      ),
    };

    const colDefs = [
      replacementActionColumn,
      createNewGeneralColumn("subjectName", "schedulePlanSubject", t, 270),
      createNewGeneralColumn("weekDayName", "dayName", t),
      createNewGeneralColumn("timeSlotNames", "schedulePlanTime", t),
      createNewGeneralColumn("groupPlanNames", "schedulePlanGroup", t),
      createNewGeneralColumn("roomNames", "schedulePlanRoom", t),
      createNewGeneralColumn("branchObjectNames", "schedulePlanBranchObject", t),
      createNewGeneralColumn("scheduleTypeName", "schedulePlanScheduleType", t),
      createNewGeneralColumn("semesterName", "schedulePlanSemester", t),
      createNewGeneralColumn("facultyName", "schedulePlanFaculty", t),
      createNewGeneralColumn("weekDayNames", "schedulePlanWeekDates", t),
    ];


    return colDefs;
  }, [t, auth, deleteSchedulePlan, searchQuery]);

  const endpoint = `/StaffPortalStaffs/GetReplacementSchedulesPaged/${auth.universityId}/${auth.userId}`;
  let paramFetch = '';

  const filterParams = useMemo(() => {
    let params = '';
    if (selectedAcademicYearId) {
      params += `&academicYearId=${selectedAcademicYearId.value}`;
    }
    if (selectedFacultyId) {
      params += `&facultyId=${selectedFacultyId.value}`;
    }
    if (selectedLevelId) {
      params += `&levelId=${selectedLevelId.value}`;
    }
    if (selectedSemesterTypeId) {
      params += `&semesterTypeId=${selectedSemesterTypeId.value}`;
    }
    if (selectedSemesterId) {
      params += `&semesterId=${selectedSemesterId.value}`;
    }
    return params;
  }, [selectedAcademicYearId, selectedFacultyId, selectedLevelId, selectedSemesterId, selectedSemesterTypeId]);

  const { data, totalRecords } = useFetchPaginatedDataWithFilters(
    axiosPrivate,
    isInitialLoadComplete ? endpoint : null,
    page + 1,
    pageSize,
    searchQuery,
    filterParams
  );
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  return (
    <ListLayout
      title={t("replacementScheduleListTitle")}
      searchQuery={searchQuery}
      onSearchChange={handleSearchChange}
      searchPlaceholder={t("searchPlaceholder")}
    >
      <div className="row justify-content-start">
        <FormSelect label={t("academicYear")} name="academicYearId" register={register} errors={errors} options={academicYears} colSize={2} isClearable={true}
          value={selectedAcademicYearId} onChange={(selectedOption) => handleSelectChange('academicYearId', selectedOption)} />
        <FormSelect label={t("faculty")} name="facultyId" register={register} errors={errors} options={faculties} colSize={3} isClearable={true}
          value={selectedFacultyId} onChange={(selectedOption) => handleSelectChange('facultyId', selectedOption)} />
        <FormSelect label="Level" name="levelId" register={register} errors={errors} options={levels} colSize={2} isClearable={true}
          value={selectedLevelId} onChange={(selectedOption) => handleSelectChange('levelId', selectedOption)} />
        <FormSelect label="Semester Type" name="semesterTypeId" register={register} errors={errors} options={semesterTypes} colSize={2} isClearable={true}
          value={selectedSemesterTypeId} onChange={(selectedOption) => handleSelectChange('semesterTypeId', selectedOption)} />
        <FormSelect label="Semester" name="semesterId" register={register} errors={errors} options={semesters} colSize={2} isClearable={true}
          value={selectedSemesterId} onChange={(selectedOption) => handleSelectChange('semesterId', selectedOption)} />
      </div>
      <DataTablePagination
        rows={data}
        columns={columns}
        rowCount={totalRecords}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        localeTextsMap={localizedTextsMap}
      />
    </ListLayout>
  );
};

export default observer(ReplacementScheduleList);
